import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { generateUUID } from '../../../../../../utils/util';

import Modal from '../../../../../common/modal/Modal';
import Button from '../../../../../common/button/Button';
import TextInput from '../../../../../common/inputs/text-input/TextInput';

import { ReactComponent as CloseIcon } from '../../../../../../resources/icons/buzz-trash.svg';

import './poll-modal.scss';

const PollModal = ({ show, setShow, poll, setPoll }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState([
    { id: generateUUID(), value: '', votes: 0 },
    { id: generateUUID(), value: '', votes: 0 },
  ]);

  const lastInputRef = useRef(undefined);

  useEffect(() => {
    if (poll) {
      setQuestion(poll.question);
      setOptions([...poll.options]);
    } else {
      setQuestion('');
      setOptions([
        { id: generateUUID(), value: '', votes: 0 },
        { id: generateUUID(), value: '', votes: 0 },
      ]);
    }
  }, [poll]);

  useEffect(() => {
    if (lastInputRef.current) {
      lastInputRef.current.scrollIntoView({ block: 'end', inline: 'nearest' });
    }
  }, [options.length]);

  const handleOptionChange = (id, value) => {
    const copyOfOptions = structuredClone(options);
    const optionIndex = copyOfOptions.findIndex((option) => option.id === id);
    copyOfOptions[optionIndex].value = value;
    setOptions(copyOfOptions);
  };

  const deleteOption = (id) => {
    const copyOfOptions = structuredClone(options);
    const optionIndex = copyOfOptions.findIndex((option) => option.id === id);
    copyOfOptions.splice(optionIndex, 1);
    setOptions(copyOfOptions);
  };

  const handleAddClick = () => {
    setOptions([...options, { id: generateUUID(), value: '', votes: 0 }]);
  };

  const handleDiscardClick = () => {
    if (poll) {
      setQuestion(poll.question);
      setOptions(poll.options);
    } else {
      setQuestion('');
      setOptions([
        { id: generateUUID(), value: '', votes: 0 },
        { id: generateUUID(), value: '', votes: 0 },
      ]);
    }

    setShow(false);
  };

  const handleSaveClick = () => {
    const poll = {
      question,
      options,
    };

    setPoll(poll);
    setShow(false);
  };

  const isSaveDisabled = () => {
    return question.trim() === '' || options.some((option) => option.value.trim() === '');
  };

  const renderOptions = () => {
    return options.map((option, index) => (
      <div className="poll-modal__option" key={option.id} ref={index === options.length - 1 ? lastInputRef : undefined}>
        <label className="poll-modal__option__label" htmlFor={`option-${option.id}`}>
          {`Option ${index + 1}:`}
        </label>
        <div className="poll-modal__option__input">
          <TextInput
            name={`option-${option.id}`}
            type="text"
            value={option.value}
            onChange={({ target }) => handleOptionChange(option.id, target.value)}
          />
          {options.length > 2 && (
            <CloseIcon className="poll-modal__option__icon" onClick={() => deleteOption(option.id)} />
          )}
        </div>
      </div>
    ));
  };

  return (
    <Modal show={show} setShow={setShow}>
      <div className="poll-modal">
        <div className="poll-modal__header">
          <p className="poll-modal__title">{poll ? 'Edit Poll' : 'Create Poll'}</p>
        </div>
        <div className="poll-modal__content">
          <div className="poll-modal__question">
            <label className="poll-modal__question__label" htmlFor="question">
              Type your question:
            </label>
            <TextInput
              name="question"
              value={question}
              onChange={({ target }) => setQuestion(target.value)}
              multiline
            />
          </div>
          <div className="poll-modal__options">{renderOptions()}</div>
          <Button onClick={handleAddClick}>Add Option</Button>
          <div className="poll-modal__actions">
            <Button onClick={handleDiscardClick} secondary>
              Discard
            </Button>
            <Button onClick={handleSaveClick} disabled={isSaveDisabled()}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

PollModal.propTypes = {
  show: PropTypes.bool,
  setShow: PropTypes.func.isRequired,
  poll: PropTypes.shape({}),
  setPoll: PropTypes.func.isRequired,
};

PollModal.defaultProps = {
  show: false,
  poll: undefined,
};

export default PollModal;
